import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`;
  return `${Math.floor(diff / 86_400_000)} days ago`;
}

function getCardClass(noInvoices, caughtUp) {
  if (noInvoices) return "border-slate-200 bg-slate-50/40";
  if (caughtUp) return "border-emerald-200 bg-emerald-50/40";
  return "border-amber-200 bg-amber-50/40";
}

function getStatusIcon(noInvoices, caughtUp) {
  if (noInvoices) return null;
  return caughtUp ? CheckCircle2 : AlertCircle;
}

export const TallySyncPanel = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/tally/status");
      setData(data);
    } catch {
      // Polling / refresh error is transient; the panel just skips this cycle.
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(true), 30_000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return null;

  const anyPending = data.firms.some((f) => f.pending_sales + f.pending_receipt > 0);

  return (
    <Card
      className="border-slate-200 shadow-none rounded-md p-5 mb-6"
      data-testid="tally-sync-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-tag">Tally Prime Sync</p>
          <h3 className="font-display text-lg font-medium text-slate-900 mt-1">
            Books of accounts
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(false)}
          disabled={refreshing}
          className="border-slate-300"
          data-testid="tally-sync-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.firms.map((f) => {
          const totalPending = f.pending_sales + f.pending_receipt;
          const noInvoices = (f.total_bills ?? 0) === 0;
          const caughtUp = !noInvoices && totalPending === 0;
          const cardClass = getCardClass(noInvoices, caughtUp);
          const StatusIcon = getStatusIcon(noInvoices, caughtUp);
          const iconClass = caughtUp ? "text-emerald-600" : "text-amber-600";
          return (
            <div
              key={f.key}
              className={`border rounded-md p-3 ${cardClass}`}
              data-testid={`tally-firm-${f.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-slate-900">{f.name}</span>
                {StatusIcon && <StatusIcon className={`w-4 h-4 ${iconClass}`} />}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Company: <span className="font-mono">{f.tally_company || "—"}</span>
              </div>
              {noInvoices ? (
                <div className="mt-2 text-xs text-slate-500 italic">
                  No invoices yet
                </div>
              ) : (
                <>
                  <div className="mt-2 flex gap-4 text-xs">
                    <span
                      className={f.pending_sales ? "text-amber-800 font-medium" : "text-slate-500"}
                      data-testid={`tally-pending-sales-${f.key}`}
                    >
                      Pending sales: {f.pending_sales}
                    </span>
                    <span
                      className={f.pending_receipt ? "text-amber-800 font-medium" : "text-slate-500"}
                      data-testid={`tally-pending-receipt-${f.key}`}
                    >
                      Pending receipts: {f.pending_receipt}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1.5">
                    Last synced: {timeAgo(f.last_synced_at)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {!data.agent_token_configured && (
        <div className="mt-3 text-xs text-rose-600">
          ⚠️ Sync agent token not configured. Ask admin to set TALLY_AGENT_TOKEN.
        </div>
      )}

      {anyPending && data.agent_token_configured && (
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          Pending vouchers wait for the local Tally sync agent to pick them up
          (poll interval ≈ 60s). Ensure the office PC has Tally open and the
          sync agent is running.
        </div>
      )}
    </Card>
  );
};
